import {
  COMPARE_SUMMARY_NAMES,
  COMPARE_TOOLTIP_MAP,
  CompareSummaryNames,
  LID_ENRICHMENT_STATUSES,
  LID_EVENT_TYPES_MAP,
  LID_STATUS_ICONS_MAP,
  LID_STATUSES,
  LidStatus,
} from '../../constants';
import { LidEnrichStatusGetDataItem, LidLineChartGetDataItem } from '../types';
import { formatPercentage } from './formatters';

import calendar from '../assets/calendar.svg';
import arrowRight from '../assets/arrowRight.svg';

export const formatCompareSummaryData = (
  name: CompareSummaryNames,
  value: number,
  withSign = false
) => {
  if (
    name === COMPARE_SUMMARY_NAMES.PERCENTAGE_OF_LID_QUALIFIED_MEMBERS ||
    name === COMPARE_SUMMARY_NAMES.MEMBER_COUNT_COMPARISON
  ) {
    return formatPercentage(value, 2, withSign);
  }

  const sign = value > 0 ? '+' : '';

  return `${withSign ? sign : ''}${value.toLocaleString()}`;
};

export const isDifferencePositive = (
  name: CompareSummaryNames,
  value: number,
  current?: number
) => {
  if (name === COMPARE_SUMMARY_NAMES.MEMBERS_NOT_QUALIFIED_AS_LID) {
    return value <= 0;
  }

  if (name === COMPARE_SUMMARY_NAMES.MEMBER_COUNT_COMPARISON) {
    return (current ?? 0) >= 90 && (current ?? 0) <= 110;
  }

  return value >= 0;
};

export const isImpactOnLIDPositive = (value: number, status: boolean) => {
  if (!status) {
    return value <= 0;
  }

  return value >= 0;
};

export const getCompareTooltipText = (
  name: CompareSummaryNames,
  value: number,
  current?: number
) => {
  const isPositive = isDifferencePositive(name, value, current);

  // show tooltip only if negative
  if (name === COMPARE_SUMMARY_NAMES.MEMBER_COUNT_COMPARISON) {
    return isPositive ? null : COMPARE_TOOLTIP_MAP[name];
  }

  if (name === COMPARE_SUMMARY_NAMES.MEMBERS_NOT_QUALIFIED_AS_LID) {
    if (value === 0) {
      return null;
    }

    return isPositive
      ? COMPARE_TOOLTIP_MAP[`${name}POSITIVE`]
      : COMPARE_TOOLTIP_MAP[`${name}NEGATIVE`];
  }

  return COMPARE_TOOLTIP_MAP[name] || null;
};

export const getLidStatus = (
  isLidConditionMet: boolean,
  lidPercentage: number
) => {
  if (isLidConditionMet) {
    return LID_STATUSES.MET;
  }

  if (lidPercentage >= 45 && lidPercentage < 51) {
    return LID_STATUSES.CLOSE;
  }

  return LID_STATUSES.NOT_MET;
};

export const getLidLineChartTooltip = ({
  date,
  lidStatus,
  countOfEvents = 0,
  isFirstEvent,
  id: selectedId,
  lidPercentageSelected,
  lidPercentagePrevious,
  difference,
  eventType,
}: LidLineChartGetDataItem & {
  date: string;
  lidStatus: LidStatus;
  countOfEvents?: number;
  isFirstEvent: boolean;
}) => {
  const isPositiveDiff = lidPercentageSelected >= lidPercentagePrevious;
  const detailsDisabled = countOfEvents < 2 || isFirstEvent;

  return `
    <div class="line-chart-tooltip">
      <div class="line-chart-tooltip--header">
        <div class="line-chart-tooltip--header--date--wrapper">
          <img src="${calendar}" alt="Date icon" class="line-chart-tooltip--header--date-icon" />
          <div class="line-chart-tooltip--header--date">
            ${date}
          </div>
        </div>
        <h4 class="line-chart-tooltip--header--title">
          ${lidPercentageSelected.toFixed(2)}%
        </h4>
        <div>LID-qualified members</div>
        <div class="line-chart-tooltip--header--status">
          <img src="${
            LID_STATUS_ICONS_MAP[lidStatus]
          }" alt="LID status icon" class="line-chart-tooltip--header--status-icon" />
          <div class="line-chart-tooltip--header--status-text">
            ${lidStatus}
          </div>
        </div>
      </div>
      <div class="line-chart-tooltip--body">
        <div class="line-chart-tooltip--body--item">
          <div>Change:</div>
          <div class="${
            isPositiveDiff ? 'positive' : 'negative'
          }">${formatPercentage(difference, 2, true)}</div>
        </div>
        <div class="line-chart-tooltip--body--item">
          Event: ${LID_EVENT_TYPES_MAP[eventType]}
        </div>
         <div class="line-chart-tooltip--body--item">
          <button
            class="line-chart-tooltip--body--button"
            data-lid-line-chart-tooltip-details-button
            data-lid-selected-id="${selectedId}"
            ${detailsDisabled ? 'disabled' : ''}
          >
            Open details
            <img src="${arrowRight}" alt="Open details icon" class="line-chart-tooltip--body--button-icon" />
          </button>
        </div>
      </div>
    </div>
  `;
};

export const processEnrichments = (
  enrichments: LidEnrichStatusGetDataItem[]
) => {
  const apiStatus = {
    MelissaApi: false,
    ClearinghouseApi_20_24: false,
    ClearinghouseApi_25_34: false,
    ClearinghouseApi_35_40: false,
    processingEnrichments: false,
  };

  enrichments?.forEach(({ type, state }) => {
    const stateIsValid =
      state === LID_ENRICHMENT_STATUSES.REQUESTED ||
      state === LID_ENRICHMENT_STATUSES.FAILED;

    if (type === 1) {
      apiStatus.MelissaApi = stateIsValid;
    } else {
      if (type === 2 || type === 6 || type === 10 || type === 14) {
        apiStatus.ClearinghouseApi_20_24 = stateIsValid;
      }
      if (type === 4 || type === 6 || type === 12 || type === 14) {
        apiStatus.ClearinghouseApi_25_34 = stateIsValid;
      }
      if (type === 8 || type === 10 || type === 12 || type === 14) {
        apiStatus.ClearinghouseApi_35_40 = stateIsValid;
      }
    }
  });

  apiStatus.processingEnrichments = enrichments.some(
    (enrichment) => enrichment.state !== LID_ENRICHMENT_STATUSES.COMPLETED
  );

  return apiStatus;
};
